import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, TextField, useTheme } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { EspelhoLegenda } from "@/components/escala/EspelhoLegenda";
import { SeloTipo } from "@/components/escala/EspelhoLinha";
import { competenciaAtual } from "@/components/escala/competenciaAtual";
import { useFiltroRelatorio } from "@/components/escala/useRelatorio";
import { plural } from "@/components/escala/relatorio";
import { estilosDoTema, rotuloDoTipo, type EstiloTipo } from "@/components/escala/tiposDia";
import { juntarComE } from "@/components/escala/cargaHoraria";
import { useAutoresDaEscala } from "@/hooks/useAutoresDaEscala";
import { useNotification } from "@/context/NotificationContext";
import {
  autorDaAlteracao,
  formatDataBR,
  formatDataHoraBR,
  relAlteracoes,
  rotuloDoMotivoLog,
  rotuloLoja,
  type LinhaAlteracao,
  type RelatorioFiltro,
  type ResumoAlteracoes,
} from "@/api/escala.service";

interface FiltroAlteracoes extends RelatorioFiltro {
  colaborador: string;
}

const RESUMO_VAZIO: ResumoAlteracoes = {
  colaboradores: 0,
  edicoes_manuais: 0,
  ausencias: 0,
  resets: 0,
  trocas_carga: 0,
  reflexos_carga: 0,
  reflexos_feriado: 0,
  sistema: 0,
};

function indicadoresAlteracoes(total: number, resumo: ResumoAlteracoes): [Indicador, Indicador, Indicador, Indicador] {
  const mais = [
    resumo.trocas_carga ? plural(resumo.trocas_carga, "troca de carga horária", "trocas de carga horária") : "",
    resumo.resets ? plural(resumo.resets, "reset") : "",
  ].filter(Boolean);
  // O reflexo vem do cadastro (carga padrão, horário da carga ou feriado), não de edição na grade.
  const reflexos = [
    resumo.reflexos_carga ? `${resumo.reflexos_carga.toLocaleString("pt-BR")} por mudança no cadastro de carga horária` : "",
    resumo.reflexos_feriado ? `${resumo.reflexos_feriado.toLocaleString("pt-BR")} por mudança no cadastro de feriado` : "",
  ]
    .filter(Boolean)
    .map((texto) => `, ${texto}`)
    .join("");
  return [
    {
      titulo: "Alterações no mês",
      valor: total,
      nota: `em ${plural(resumo.colaboradores, "colaborador", "colaboradores")}${reflexos}`,
    },
    {
      titulo: "Edições manuais",
      valor: resumo.edicoes_manuais,
      nota: mais.length ? `mais ${juntarComE(mais)} da escala` : "feitas na grade da escala",
    },
    { titulo: "Por ausência", valor: resumo.ausencias, nota: "lançamento ou cancelamento" },
    { titulo: "Pela sincronização", valor: resumo.sistema, nota: "sem usuário, vindas da integração" },
  ];
}

// O tipo de antes e o de depois, com as cores do espelho; célula vazia vira traço.
function DePara({ de, para, estilos }: { de: string; para: string; estilos: Record<string, EstiloTipo> }) {
  const selo = (tipo: string) =>
    tipo ? (
      <Box title={rotuloDoTipo(tipo)}>
        <SeloTipo tipo={tipo} estilo={estilos[tipo] ?? estilos[""]!} />
      </Box>
    ) : (
      <Box sx={{ width: 30, textAlign: "center", color: "text.disabled" }}>—</Box>
    );
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {selo(de)}
      <ArrowForward sx={{ fontSize: 16, color: "text.secondary" }} />
      {selo(para)}
    </Stack>
  );
}

// Horário de antes e de depois numa escala com carga: "Comercial 08:00–18:00 → Tarde 13:00–22:00".
const detalheDePara = (l: LinhaAlteracao) =>
  l.detalhe_de || l.detalhe_para ? `${l.detalhe_de || "—"} → ${l.detalhe_para || "—"}` : "—";

// Log de alterações de célula cruzado por loja e competência. Paginado no
// servidor: o Exportar leva a página carregada; o resumo conta o filtro inteiro.
export function EscalaRelAlteracoesTab() {
  const { notify } = useNotification();
  const theme = useTheme();
  const f = useFiltroRelatorio<FiltroAlteracoes>({ ...competenciaAtual(), colaborador: "" });
  const { clientId, filtros, lojas } = f;
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = f.paginacao;
  const autores = useAutoresDaEscala(clientId);

  const [linhas, setLinhas] = useState<LinhaAlteracao[]>([]);
  const [resumo, setResumo] = useState<ResumoAlteracoes>(RESUMO_VAZIO);
  const [carregouAlgumaVez, setCarregouAlgumaVez] = useState(false);
  const chamada = useRef(0);

  useEffect(() => {
    const minha = ++chamada.current;
    relAlteracoes(
      {
        ano: filtros.ano,
        mes: filtros.mes,
        loja_id: filtros.loja_id,
        colaborador: filtros.colaborador || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      },
      clientId,
    )
      .then((result) => {
        if (minha !== chamada.current) return;
        setLinhas(result.items ?? []);
        setTotal(result.total ?? 0);
        setResumo(result.resumo ?? RESUMO_VAZIO);
      })
      .catch(() => {
        if (minha === chamada.current) notify("Erro ao carregar as alterações", "error");
      })
      .finally(() => {
        if (minha === chamada.current) setCarregouAlgumaVez(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filtros, page, rowsPerPage]);

  const estilos = useMemo(() => estilosDoTema(theme), [theme]);
  const indicadores = useMemo(() => indicadoresAlteracoes(total, resumo), [total, resumo]);

  const columns: Column<LinhaAlteracao>[] = [
    { key: "created_at", label: "Data e Hora", render: (l) => formatDataHoraBR(l.created_at), exportValue: (l) => formatDataHoraBR(l.created_at) },
    { key: "user_id", label: "Autor", render: (l) => autorDaAlteracao(l, autores), exportValue: (l) => autorDaAlteracao(l, autores) },
    { key: "colaborador_nome", label: "Colaborador" },
    { key: "escala_nome", label: "Escala" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(lojas, l.loja_id), exportValue: (l) => rotuloLoja(lojas, l.loja_id) },
    { key: "data", label: "Dia Alterado", render: (l) => formatDataBR(l.data), exportValue: (l) => formatDataBR(l.data) },
    { key: "de", label: "De → Para", render: (l) => <DePara de={l.de} para={l.para} estilos={estilos} /> },
    // Só quando a página tem alteração de escala com carga horária.
    ...(linhas.some((l) => l.detalhe_de || l.detalhe_para)
      ? [{ key: "detalhe", label: "Carga horária", render: (l: LinhaAlteracao) => detalheDePara(l) }]
      : []),
    { key: "motivo", label: "Motivo", render: (l) => rotuloDoMotivoLog(l.motivo), exportValue: (l) => rotuloDoMotivoLog(l.motivo) },
  ];

  // O Excel separa De e Para em duas colunas; a tela junta as duas numa só.
  const colunasExportadas: Column<LinhaAlteracao>[] = columns.flatMap((c) =>
    c.key === "de"
      ? [
          { key: "de", label: "De", exportValue: (l: LinhaAlteracao) => rotuloDoTipo(l.de) },
          { key: "para", label: "Para", exportValue: (l: LinhaAlteracao) => rotuloDoTipo(l.para) },
        ]
      : c.key === "detalhe"
        ? [
            { key: "detalhe_de", label: "Carga anterior", exportValue: (l: LinhaAlteracao) => l.detalhe_de ?? "" },
            { key: "detalhe_para", label: "Carga nova", exportValue: (l: LinhaAlteracao) => l.detalhe_para ?? "" },
          ]
        : [c],
  );

  return (
    <RelatorioLayout
      carregando={!carregouAlgumaVez}
      filtro={
        <FiltroRelatorio estado={f} comCargo={false}>
          <TextField
            label="Colaborador"
            size="small"
            fullWidth
            value={f.inputValues.colaborador}
            onChange={(e) => f.setInputValues((v) => ({ ...v, colaborador: e.target.value }))}
          />
        </FiltroRelatorio>
      }
      indicadores={indicadores}
      exportacao={{ colunas: colunasExportadas, linhas, arquivo: `alteracoes-pagina-${page + 1}`, aba: "Alterações" }}
      competencia={filtros}
      legenda={<EspelhoLegenda feriado={false} />}
    >
      <DataTable
        data={linhas}
        columns={columns}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </RelatorioLayout>
  );
}
